import { Component, OnInit } from '@angular/core';
import { DatePipe, TitleCasePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { AuthService } from '@core/service/auth.service';
import { RestClientService } from '@core/service/rest-client.service';
import { IntegerFormatPipe } from '@core/pipe/integer-format.pipe';

interface AccesRapide {
  path: string;
  libelle: string;
  detail: string;
  icone: string;
  /** Droits requis : module parent puis écran, comme dans la sidebar. */
  codes: string[];
  option?: 'restaurant' | 'boutique';
}

const ACCES_RAPIDES: AccesRapide[] = [
  { path: '/planning', libelle: 'Planning', detail: 'Chambres et espaces', icone: 'calendar_view_month', codes: ['PLANNING'] },
  { path: '/service-caisse/pass', libelle: 'Pass', detail: "Séjours à l'heure", icone: 'timer', codes: ['SERVICE_CAISSE', 'SERVICE_PASS'] },
  { path: '/service-caisse/nuitee', libelle: 'Nuitée', detail: 'Nuits en cours', icone: 'bedtime', codes: ['SERVICE_CAISSE', 'SERVICE_NUITE'] },
  { path: '/service-caisse/journee', libelle: 'Journée', detail: 'Séjours de jour', icone: 'wb_sunny', codes: ['SERVICE_CAISSE', 'SERVICE_JOURNEE'] },
  { path: '/service-caisse/sejour', libelle: 'Séjour', detail: 'Séjours de plusieurs jours', icone: 'luggage', codes: ['SERVICE_CAISSE', 'SERVICE_SEJOUR'] },
  { path: '/accueil/reservation', libelle: 'Réservations', detail: 'Liste et calendrier', icone: 'event_available', codes: ['ACCUEIL', 'RESERVATION'] },
  { path: '/accueil/hebergement', libelle: 'Hébergements', detail: 'Tous les hébergements', icone: 'bed', codes: ['ACCUEIL', 'HEBERGEMENT'] },
  { path: '/restaurant/commande-restau', libelle: 'Restaurant', detail: 'Commandes', icone: 'restaurant', codes: ['RESTAURANT', 'COMMANDE_RESTAU'], option: 'restaurant' },
  { path: '/boutique/commande-boutique', libelle: 'Boutique', detail: 'Commandes', icone: 'storefront', codes: ['BOUTIQUE', 'COMMANDE_BOUTIQUE'], option: 'boutique' },
  { path: '/tresorerie/demande-espece', libelle: "Demandes d'espèces", detail: 'Trésorerie', icone: 'request_quote', codes: ['TRESORERIE', 'DEMANDE_ESPECE'] },
  { path: '/stock/commande', libelle: 'Stock', detail: 'Commandes fournisseurs', icone: 'inventory_2', codes: ['GESTION_DE_STOCK', 'COMMANDE'] },
];

@Component({
  selector: 'app-home',
  imports: [DatePipe, TitleCasePipe, RouterLink, IntegerFormatPipe],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent implements OnInit {
  user: any = {};
  readonly aujourdhui = new Date();
  accesRapides: AccesRapide[] = [];

  peutVoirTableauDeBord = false;
  chargement = false;
  indicateurs: any = null;
  caisses: any = null;
  alertes: any[] = [];

  constructor(
    private authService: AuthService,
    private restClient: RestClientService
  ) {}

  ngOnInit() {
    this.user = this.authService.getCurrentUser();
    this.accesRapides = ACCES_RAPIDES.filter((acces) => this.estAccessible(acces));
    this.peutVoirTableauDeBord = this.authService.hasAccess('TABLEAU_DE_BORD');
    if (this.peutVoirTableauDeBord) this.chargerJournee();
  }

  get salutation(): string {
    return new Date().getHours() < 18 ? 'Bonjour' : 'Bonsoir';
  }

  /** Variante de badge pour un type d'alerte renvoyé par l'API. */
  varianteAlerte(type: string): string {
    return ({ danger: 'err', warning: 'warn', success: 'ok' } as Record<string, string>)[type] ?? 'info';
  }

  private estAccessible(acces: AccesRapide): boolean {
    if (acces.option === 'restaurant' && !this.authService.hasRestaurant()) return false;
    if (acces.option === 'boutique' && !this.authService.hasBoutique()) return false;
    return acces.codes.every((code) => this.authService.hasAccess(code));
  }

  private chargerJournee() {
    const now = new Date();
    const jour = (d: Date) => d.toISOString().split('T')[0];
    const request = {
      user_id: this.user?.id,
      hotel_id: this.user?.hotel_id,
      periode: {
        date_debut: jour(new Date(now.getFullYear(), now.getMonth(), 1)),
        date_fin: jour(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
      },
    };
    this.chargement = true;
    this.restClient.post('kpi/adminDashboard', request).subscribe({
      next: (res: any) => {
        if (!res?.hasError && res?.item) {
          this.indicateurs = res.item.kpis ?? null;
          this.caisses = res.item.caisseDuJour ?? null;
          this.alertes = res.item.alerts ?? [];
        }
        this.chargement = false;
      },
      error: () => (this.chargement = false),
    });
  }
}
